import { useState, useEffect, useRef, Fragment } from "react";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Separator } from "../../components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/ui/collapsible";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { toast } from "sonner";
import { DeleteConfirmModal } from "../../components/DeleteConfirmModal";
import { MultiSelect } from "../../components/ui/multi-select";
import {
  Plus, Search, Phone, Mail, Calendar, Filter, X,
  Pencil, Trash2, User, Loader2, Tag, ChevronDown, ChevronLeft, ChevronRight, Package, Users
} from "lucide-react";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { WhatsApp } from "../../components/ui/whatsapp";

const LEAD_SOURCES = ["Walkin", "Call", "Website", "Facebook", "Instagram", "Reference", "Advertisement", "Others"];
const LEAD_STATUSES = ["New", "Contacted", "Interested", "Not Interested", "Not Reachable", "Need Followup", "Converted", "Closed"];
const LEAD_PRIORITIES = ["High", "Medium", "Low"];

const PRIORITY_COLORS = {
  High: "bg-red-500/15 text-red-500 border-red-500/20",
  Medium: "bg-amber-500/15 text-amber-500 border-amber-500/20",
  Low: "bg-blue-500/15 text-blue-500 border-blue-500/20",
};

const STATUS_COLORS = {
  New: "bg-blue-500/15 text-blue-500 border-blue-500/20",
  Contacted: "bg-yellow-500/15 text-yellow-500 border-yellow-500/20",
  Interested: "bg-green-500/15 text-green-500 border-green-500/20",
  "Not Interested": "bg-zinc-500/15 text-zinc-400 border-zinc-500/20",
  "Not Reachable": "bg-orange-500/15 text-orange-500 border-orange-500/20",
  "Need Followup": "bg-purple-500/15 text-purple-500 border-purple-500/20",
  Converted: "bg-emerald-500/15 text-emerald-500 border-emerald-500/20",
  Closed: "bg-zinc-700/15 text-zinc-500 border-zinc-700/20",
};

const EMPTY_FILTERS = { search: "", status: "", source: "", priority: "", groupId: "", assignedStaffId: "" };

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 640px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

export default function AllLeads() {
  const { currentBranch } = useAuth();
  const isDesktop = useIsDesktop();
  const newGroupInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // Modal states
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showFollowupForm, setShowFollowupForm] = useState(false);
  const [followupLeadId, setFollowupLeadId] = useState(null);
  const [followupLoading, setFollowupLoading] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const toggleRow = (id) => setExpandedRowId(prev => (prev === id ? null : id));

  // Filter Sheet
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });

  // Dropdown data
  const [groups, setGroups] = useState([]);
  const [staffList, setStaffList] = useState([]);

  // Group creation inline
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [groupLoading, setGroupLoading] = useState(false);

  // Lead form
  const initialFormState = {
    name: "", phone: "", email: "",
    groupIds: [], priority: "Medium", source: "Walkin", otherSource: "",
    interest: "", reference: "", assignedStaffIds: [], status: "New", notes: "", branchId: "",
  };
  const [form, setForm] = useState(initialFormState);

  // Followup form
  const [followupForm, setFollowupForm] = useState({ scheduledDate: "", scheduledTime: "", notes: "" });

  useEffect(() => { fetchLeads(undefined, page, limit); }, [page, limit, currentBranch]);
  useEffect(() => { fetchDropdownData(); }, [currentBranch]);
  useEffect(() => {
    if (showNewGroup) setTimeout(() => newGroupInputRef.current?.focus(), 50);
  }, [showNewGroup]);

  const fetchDropdownData = async () => {
    try {
      const params = new URLSearchParams();
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      const [groupsRes, staffRes] = await Promise.all([
        api.get(`/lead-management/groups?${params}`),
        api.get(`/lead-management/staff/list?${params}`),
      ]);
      setGroups(groupsRes.data.groups || []);
      setStaffList(staffRes.data.staff || []);
    } catch (err) { console.error("Failed to fetch dropdown data:", err); }
  };

  const fetchLeads = async (overrideFilters, currentPage = page, currentLimit = limit) => {
    setLoading(true);
    const active = overrideFilters ?? filters;
    try {
      const params = new URLSearchParams({ page: currentPage.toString(), limit: currentLimit.toString() });
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      if (active.search) params.append("search", active.search);
      if (active.status) params.append("status", active.status);
      if (active.source) params.append("source", active.source);
      if (active.priority) params.append("priority", active.priority);
      if (active.groupId) params.append("groupId", active.groupId);
      if (active.assignedStaffId) params.append("assignedStaffId", active.assignedStaffId);
      const res = await api.get(`/lead-management?${params}`);
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch leads:", err);
      toast.error("Failed to load leads");
    } finally { setLoading(false); }
  };

  const handleSearch = () => { setPage(1); fetchLeads(undefined, 1, limit); setShowFilters(false); };

  const clearFilters = () => {
    const cleared = { ...EMPTY_FILTERS };
    setFilters(cleared);
    setPage(1);
    fetchLeads(cleared, 1, limit);
    setShowFilters(false);
  };

  const activeFilterCount = Object.values(filters).filter(v =>
    Array.isArray(v) ? v.length > 0 : !!v
  ).length;

  // ── CRUD ─────────────────────────────────────────────────────────────
  const handleOpenForm = (lead = null) => {
    setShowNewGroup(false);
    setNewGroupName("");
    if (lead) {
      setEditing(lead);
      setForm({
        name: lead.name || "", phone: lead.phone || "", email: lead.email || "",
        groupIds: lead.groupIds || [], priority: lead.priority || "Medium",
        source: lead.source || "Walkin", otherSource: lead.otherSource || "",
        interest: lead.interest || "", reference: lead.reference || "",
        assignedStaffIds: lead.assignedStaffIds || [], status: lead.status || "New",
        notes: lead.notes || "", branchId: lead.branchId || currentBranch?.id || "",
      });
    } else {
      setEditing(null);
      setForm({ ...initialFormState, branchId: currentBranch?.id || "" });
    }
    setShowForm(true);
  };

  const handleSaveLead = async () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error("Name and Phone are required"); return; }
    setFormLoading(true);
    try {
      if (editing) {
        await api.put(`/lead-management/${editing.id}`, form);
        toast.success("Lead updated");
      } else {
        await api.post("/lead-management", form);
        toast.success("Lead created");
      }
      setShowForm(false);
      setEditing(null);
      setForm(initialFormState);
      fetchLeads(undefined, page, limit);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save lead");
    } finally { setFormLoading(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/lead-management/${deleteTarget.id}`);
      toast.success("Lead deleted");
      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchLeads(undefined, page, limit);
    } catch { toast.error("Failed to delete lead"); }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    setGroupLoading(true);
    try {
      const params = new URLSearchParams();
      if (currentBranch?.id) params.append("branchId", currentBranch.id);
      const res = await api.post(`/lead-management/groups?${params}`, { name: newGroupName.trim() });
      const newGroup = res.data.group;
      setGroups(prev => [...prev, newGroup]);
      setForm(f => ({ ...f, groupIds: [...f.groupIds, newGroup.id] }));
      setNewGroupName("");
      setShowNewGroup(false);
      toast.success(`Group "${newGroup.name}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create group");
    } finally { setGroupLoading(false); }
  };

  const handleOpenFollowupForm = (leadId) => {
    setFollowupLeadId(leadId);
    setFollowupForm({ scheduledDate: "", scheduledTime: "", notes: "" });
    setShowFollowupForm(true);
  };

  const handleSaveFollowup = async () => {
    if (!followupForm.scheduledDate) { toast.error("Schedule date is required"); return; }
    if (!followupForm.scheduledTime) { toast.error("Schedule time is required"); return; }
    setFollowupLoading(true);
    try {
      await api.post("/lead-management/followups", { leadId: followupLeadId, ...followupForm });
      toast.success("Followup scheduled");
      setShowFollowupForm(false);
      setFollowupLeadId(null);
      fetchLeads(undefined, page, limit);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to schedule followup");
    } finally {
      setFollowupLoading(false);
    }
  };

  const toggleGroupId = (id) => setForm(f => ({ ...f, groupIds: f.groupIds.includes(id) ? f.groupIds.filter(x => x !== id) : [...f.groupIds, id] }));
  const toggleStaffId = (id) => setForm(f => ({ ...f, assignedStaffIds: f.assignedStaffIds.includes(id) ? f.assignedStaffIds.filter(x => x !== id) : [...f.assignedStaffIds, id] }));

  const selectedGroups = groups.filter(g => form.groupIds.includes(g.id));
  const unselectedGroups = groups.filter(g => !form.groupIds.includes(g.id));
  const totalPages = Math.ceil(total / limit) || 1;

  const renderLeadDetails = (lead) => {
    return (
      <div className="text-sm text-foreground bg-secondary/5">
        {/* Action Bar */}
        {lead.phone && (
          <div className="grid grid-cols-2 gap-2 p-2 border-b border-border">
            <a
              href={`tel:${lead.phone}`}
              onClick={(e) => e.stopPropagation()}
              className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-sky-400 dark:hover:border-sky-500 hover:shadow-md active:scale-95 px-2"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-50 dark:bg-sky-950/50 transition-colors group-hover:bg-sky-100 dark:group-hover:bg-sky-900/50">
                <Phone className="h-4 w-4 text-sky-600 dark:text-sky-400" />
              </div>
              <div className="flex flex-col items-start leading-tight min-w-0">
                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                  Mobile Call
                </span>
                <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                  Call Now
                </span>
              </div>
            </a>
            <a
              href={`https://wa.me/${lead.phone}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="group flex flex-1 items-center justify-center gap-2 rounded-xl border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-2.5 transition-all hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-md active:scale-95 px-2"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/50 transition-colors group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50">
                <WhatsApp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="flex flex-col items-start leading-tight min-w-0">
                <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 dark:text-zinc-500 truncate w-full">
                  Message
                </span>
                <span className="text-sm font-black text-slate-900 dark:text-zinc-100 truncate w-full">
                  WhatsApp
                </span>
              </div>
            </a>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 p-2 border-b border-border/40 bg-secondary/10">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5 bg-background hover:bg-secondary/40 transition-colors text-primary border-primary/20"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenFollowupForm(lead.id);
            }}
          >
            <Calendar className="h-3.5 w-3.5" />
            <span>Schedule Followup</span>
            {lead.followupCount > 0 && (
              <Badge variant="secondary" className="ml-1.5 h-4 min-w-4 px-1 rounded-full text-[9px] bg-primary text-primary-foreground">
                {lead.followupCount}
              </Badge>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5 bg-background hover:bg-secondary/40 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenForm(lead);
            }}
          >
            <Pencil className="h-3.5 w-3.5" />
            <span className="hidden md:block">Edit Lead</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5 bg-background hover:bg-secondary/40 transition-colors text-destructive hover:text-destructive border-red-500/20"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget(lead);
              setShowDeleteModal(true);
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
            <span className="hidden md:block">Delete Lead</span>
          </Button>
        </div>

        {/* 3-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4">
          {/* Column 1: Lead Info & Contact */}
          <div className="space-y-1">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Lead & Contact Info</p>
            <div className="text-xs space-y-1 mt-1 text-muted-foreground">
              <p><span className="font-mono text-muted-foreground">Name:</span> <span className="font-semibold text-foreground">{lead.name || "—"}</span></p>
              <p><span className="font-mono text-muted-foreground">Phone:</span> <span className="font-mono">{lead.phone || "—"}</span></p>
              <p>
                <span className="font-mono text-muted-foreground">Email:</span>{" "}
                {lead.email ? (
                  <a href={`mailto:${lead.email}`} className="text-primary hover:underline font-medium">
                    {lead.email}
                  </a>
                ) : (
                  <span className="italic">—</span>
                )}
              </p>
              <div>
                <span className="font-mono text-muted-foreground">Source:</span>{" "}
                <Badge variant="outline" className="text-[10px] py-0 px-1 font-mono uppercase">
                  {lead.source} {lead.source === "Others" && lead.otherSource ? `(${lead.otherSource})` : ""}
                </Badge>
              </div>
            </div>
          </div>

          {/* Column 2: Priority & Assignment */}
          <div className="space-y-1">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Priority & Grouping</p>
            <div className="text-xs space-y-1.5 mt-1 text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-muted-foreground">Priority:</span>
                <Badge className={`text-[9px] py-0 px-1.5 font-mono border ${PRIORITY_COLORS[lead.priority] || ""}`}>
                  {lead.priority}
                </Badge>
              </div>
              <div>
                <span className="font-mono text-muted-foreground block mb-0.5">Groups:</span>
                <div className="flex flex-wrap gap-1">
                  {lead.groupNames?.length > 0 ? (
                    lead.groupNames.map((n, i) => (
                      <Badge key={i} variant="secondary" className="text-[10px] px-1.5 py-0 leading-none">
                        {n}
                      </Badge>
                    ))
                  ) : (
                    <span className="italic text-muted-foreground font-normal">None</span>
                  )}
                </div>
              </div>
              <p className="flex items-center gap-1.5">
                <span className="font-mono text-muted-foreground">Assigned Staff:</span>
                <span className="font-medium text-foreground">
                  {lead.assignedStaffNames?.length > 0 ? lead.assignedStaffNames.join(", ") : "Unassigned"}
                </span>
              </p>
            </div>
          </div>

          {/* Column 3: Internal Notes */}
          <div className="space-y-1">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Internal Notes</p>
            <p className="text-xs leading-relaxed text-muted-foreground bg-secondary/20 p-2.5 rounded-lg border border-border/40 whitespace-pre-wrap mt-1">
              {lead.notes || "No notes available for this lead."}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 space-y-1.5 md:space-y-3" data-testid="all-leads-page">

      {/* ── Header ── */}
      <div className="flex sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-3xl font-black font-heading tracking-tight uppercase">All Leads</h1>
          <p className="text-sm tracking-tight text-muted-foreground">{total} lead{total !== 1 ? "s" : ""} found</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={activeFilterCount > 0 ? "secondary" : "outline"} size="sm"
            onClick={() => setShowFilters(true)}
            className="font-heading uppercase tracking-wide text-xs"
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-4 w-4 mr-2" /><span className="hidden md:block">Filters</span>
            {activeFilterCount > 0 && (
              <Badge className="ml-1.5 bg-primary text-primary-foreground h-4 min-w-4 px-1 rounded-full text-[10px]">{activeFilterCount}</Badge>
            )}
          </Button>
          <Button
            size="sm" onClick={() => handleOpenForm()}
            className="font-heading uppercase tracking-wide text-xs"
            data-testid="add-lead-btn"
          >
            <Plus className="h-4 w-4 mr-2" />Add Lead
          </Button>
        </div>
      </div>

      {/* ── Filter Sheet ── */}
      <Sheet open={showFilters} onOpenChange={setShowFilters}>
        <SheetContent
          side={isDesktop ? "right" : "bottom"}
          className={isDesktop
            ? "w-[380px] flex flex-col p-0 bg-card border-border"
            : "rounded-t-2xl flex flex-col p-0 max-h-[92vh] bg-card border-border"
          }
        >
          <SheetHeader className="px-6 pt-5 pb-4 border-b border-border shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-heading uppercase tracking-wide text-base flex items-center gap-2">
                <Filter className="h-4 w-4" /> Filters
                {activeFilterCount > 0 && (
                  <Badge className="bg-primary text-primary-foreground text-[10px] px-1.5">{activeFilterCount} active</Badge>
                )}
              </SheetTitle>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground text-xs h-7">
                  <X className="h-3.5 w-3.5 mr-1" />Clear all
                </Button>
              )}
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Name / Phone..." value={filters.search}
                  onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  className="pl-9" data-testid="filter-search" />
              </div>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Status</Label>
                <Select value={filters.status || "all"} onValueChange={(v) => setFilters(f => ({ ...f, status: v === "all" ? "" : v }))}>
                  <SelectTrigger data-testid="filter-status"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {LEAD_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Priority</Label>
                <Select value={filters.priority || "all"} onValueChange={(v) => setFilters(f => ({ ...f, priority: v === "all" ? "" : v }))}>
                  <SelectTrigger data-testid="filter-priority"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    {LEAD_PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Source</Label>
              <Select value={filters.source || "all"} onValueChange={(v) => setFilters(f => ({ ...f, source: v === "all" ? "" : v }))}>
                <SelectTrigger data-testid="filter-source"><SelectValue placeholder="All Sources" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {LEAD_SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Separator />
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Group</Label>
              <MultiSelect
                options={groups.map(g => ({ label: g.name, value: g.id }))}
                selected={filters.groupId ? [filters.groupId] : []}
                onChange={(vals) => setFilters(f => ({ ...f, groupId: vals[0] || "" }))}
                placeholder="All Groups"
                single={true}
                className="h-auto"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Assigned Staff</Label>
              <MultiSelect
                options={staffList.map(s => ({ label: s.name, value: s.id }))}
                selected={filters.assignedStaffId ? [filters.assignedStaffId] : []}
                onChange={(vals) => setFilters(f => ({ ...f, assignedStaffId: vals[0] || "" }))}
                placeholder="All Staff"
                single={true}
                className="h-auto"
              />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-border bg-card shrink-0">
            <Button className="w-full font-heading uppercase tracking-wide text-xs" onClick={handleSearch}>
              <Search className="h-4 w-4 mr-2" />Apply Filters
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Lead List Card ── */}
      <Card className="bg-card border-x-0 md:border border-y border-border overflow-hidden flex flex-col flex-1 min-h-0 md:mx-0 rounded-lg">
        <div className="flex-1">
          {loading ? (
            <div className="py-24 flex flex-col items-center justify-center gap-3">
              <Loader2 className="h-10 w-10 animate-spin text-primary opacity-50" />
              <p className="text-sm text-muted-foreground animate-pulse font-mono tracking-widest uppercase text-[10px]">Loading leads...</p>
            </div>
          ) : leads.length === 0 ? (
            <div className="py-24 text-center text-muted-foreground">
              <User className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <h3 className="text-lg font-heading uppercase tracking-tight text-foreground mb-1">No leads found</h3>
              <p className="text-sm mb-6 max-w-[250px] mx-auto text-muted-foreground/80">Try adjusting your filters or add a new lead to get started.</p>
              <Button variant="outline" size="sm" onClick={() => handleOpenForm()} className="font-heading uppercase tracking-wide text-xs">
                <Plus className="h-4 w-4 mr-2" />Add First Lead
              </Button>
            </div>
          ) : (
            <>
              {/* ── Mobile collapsible cards (hidden md+) ── */}
              <div className="md:hidden overflow-y-auto flex-1">
                {leads.map((lead, idx) => (
                  <Collapsible
                    key={lead.id}
                    open={expandedRowId === lead.id}
                    onOpenChange={() => toggleRow(lead.id)}
                    asChild
                  >
                    <div className={`border-b border-border hover:bg-secondary/10 transition-colors`}>
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center justify-between gap-2 cursor-pointer select-none px-3 py-2">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="flex items-center justify-between flex-col shrink-0">
                              <Avatar className="h-8 w-8 border border-border">
                                <AvatarFallback className="bg-primary/10 text-primary text-xs font-heading font-bold">
                                  {lead.name?.[0]?.toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <Button variant="ghost" size="icon" className="h-4 w-4 p-0 text-muted-foreground hover:text-foreground shrink-0 mt-0.5">
                                <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${expandedRowId === lead.id ? "rotate-180" : ""}`} />
                              </Button>
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-sm leading-tight truncate">
                                {lead.name}
                              </p>
                              <p className="text-[11px] text-muted-foreground font-mono mt-1 flex items-center gap-1">
                                <Phone className="h-3 w-3" /> {lead.phone}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="flex items-end flex-col gap-1 text-right">
                              <Badge className={`${STATUS_COLORS[lead.status]} border text-[9px] font-mono uppercase px-1.5 py-0`}>
                                {lead.status}
                              </Badge>
                              <Badge className={`${PRIORITY_COLORS[lead.priority]} border text-[9px] font-mono uppercase px-1.5 py-0`}>
                                {lead.priority}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="border-t border-border/50 bg-secondary/5 animate-in fade-in slide-in-from-top-1 duration-200">
                          {renderLeadDetails(lead)}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                ))}
              </div>

              {/* ── Desktop table (hidden below md) ── */}
              <div className="hidden md:block overflow-auto flex-1 animate-in fade-in duration-200">
                <Table>
                  <TableHeader className="sticky top-0 bg-card z-10">
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Lead</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Groups</TableHead>
                      <TableHead>Assigned Staff</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead) => (
                      <Fragment key={lead.id}>
                        <TableRow className="group cursor-pointer hover:bg-secondary/50 border-b border-border" onClick={() => toggleRow(lead.id)}>
                          <TableCell className="w-10">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); toggleRow(lead.id); }}>
                              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expandedRowId === lead.id ? "rotate-180" : ""}`} />
                            </Button>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-9 w-9 border border-border">
                                <AvatarFallback className="bg-primary/10 text-primary text-xs font-heading">
                                  {lead.name?.[0]?.toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-semibold text-sm leading-tight text-foreground">{lead.name || "—"}</p>
                                <p className="text-[10px] text-muted-foreground font-mono">{lead.email || "—"}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{lead.phone}</TableCell>
                          <TableCell>
                            <Badge className={`${STATUS_COLORS[lead.status]} border text-[10px] font-mono uppercase px-2`}>
                              {lead.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${PRIORITY_COLORS[lead.priority]} border text-[10px] font-mono uppercase px-2`}>
                              {lead.priority}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-[10px] font-mono uppercase">
                              {lead.source}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1 max-w-[150px]">
                              {lead.groupNames?.length > 0 ? (
                                lead.groupNames.map((n, i) => (
                                  <Badge key={i} variant="secondary" className="text-[9px] px-1.5 py-0">
                                    {n}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-xs text-muted-foreground italic">—</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm font-medium">
                            {lead.assignedStaffNames?.length > 0 ? (
                              <span className="truncate max-w-[120px] block" title={lead.assignedStaffNames.join(", ")}>
                                {lead.assignedStaffNames.join(", ")}
                              </span>
                            ) : (
                              <span className="text-muted-foreground font-normal italic">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                        {expandedRowId === lead.id && (
                          <TableRow className="bg-secondary/30 hover:bg-secondary/30 border-b border-border animate-in fade-in duration-200">
                            <TableCell colSpan={8} className="p-0">
                              {renderLeadDetails(lead)}
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>

        {/* ── Pagination ── */}
        <div className="flex flex-row items-center justify-between gap-4 p-4 border-t border-border">
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="font-mono hidden sm:block">{total} total</span>
            <div className="flex items-center gap-2">
              <span className="text-xs">Show:</span>
              <Select value={String(limit)} onValueChange={(v) => { setLimit(Number(v)); setPage(1); }}>
                <SelectTrigger className="w-20 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="sm:text-sm text-xs text-muted-foreground px-2">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page * limit >= total} onClick={() => setPage(page + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* ═════════════════════════════════════════════════════════════════════
          Add / Edit Lead Dialog
         ═════════════════════════════════════════════════════════════════════ */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!formLoading) setShowForm(o); }}>
        <DialogContent className="w-[calc(100vw-24px)] sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] p-0 overflow-hidden flex flex-col bg-card border-border">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-border shrink-0">
            <DialogTitle className="font-heading uppercase tracking-wide text-lg">
              {editing ? "Edit Lead" : "Add New Lead"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-1">
            <div className="space-y-4 px-5 py-4">

              {/* Name + Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Name <span className="text-destructive">*</span></Label>
                  <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="Full name" disabled={formLoading} data-testid="lead-form-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Phone <span className="text-destructive">*</span></Label>
                  <PhoneInput
                    defaultCountry="in"
                    value={form.phone}
                    onChange={(phone) => setForm(f => ({ ...f, phone }))}
                    disabled={formLoading}
                    data-testid="lead-form-phone"
                    className="w-full mt-1"
                    inputClassName="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-primary/30"
                  />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <Label className="text-xs">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="Email address" disabled={formLoading} data-testid="lead-form-email" />
              </div>

              {/* Groups */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" />Groups</Label>
                  {!showNewGroup && (
                    <Button type="button" variant="ghost" size="sm" className="h-6 text-[11px] text-primary px-2"
                      onClick={() => setShowNewGroup(true)} disabled={formLoading}>
                      <Plus className="h-3 w-3 mr-1" />New Group
                    </Button>
                  )}
                </div>
                <MultiSelect
                  options={groups.map(g => ({ label: g.name, value: g.id }))}
                  selected={form.groupIds}
                  onChange={(vals) => setForm(f => ({ ...f, groupIds: vals }))}
                  placeholder="Select Groups..."
                  className="h-auto"
                />
                {groups.length === 0 && !showNewGroup && (
                  <p className="text-xs text-muted-foreground italic">No groups yet — create one below.</p>
                )}
                {showNewGroup && (
                  <div className="flex items-center gap-2 mt-1 p-3 rounded-lg border border-dashed border-primary/40 bg-primary/5">
                    <Input ref={newGroupInputRef} value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleCreateGroup(); } if (e.key === "Escape") { setShowNewGroup(false); setNewGroupName(""); } }}
                      placeholder="Group name..." className="h-8 text-xs flex-1" disabled={groupLoading} />
                    <Button type="button" size="sm" className="h-8 text-xs shrink-0" onClick={handleCreateGroup}
                      disabled={groupLoading || !newGroupName.trim()}>
                      {groupLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" />Add</>}
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-muted-foreground"
                      onClick={() => { setShowNewGroup(false); setNewGroupName(""); }} disabled={groupLoading}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>

              <Separator />

              {/* Priority / Source / Status */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm(f => ({ ...f, priority: v }))} disabled={formLoading}>
                    <SelectTrigger data-testid="lead-form-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{LEAD_PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Source</Label>
                  <Select value={form.source} onValueChange={(v) => setForm(f => ({ ...f, source: v }))} disabled={formLoading}>
                    <SelectTrigger data-testid="lead-form-source"><SelectValue /></SelectTrigger>
                    <SelectContent>{LEAD_SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))} disabled={formLoading}>
                    <SelectTrigger data-testid="lead-form-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{LEAD_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {form.source === "Others" && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Other Source</Label>
                  <Input value={form.otherSource} onChange={(e) => setForm(f => ({ ...f, otherSource: e.target.value }))}
                    placeholder="Specify source" disabled={formLoading} />
                </div>
              )}

              {/* Interest / Reference */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Interest / Goal</Label>
                  <Input value={form.interest} onChange={(e) => setForm(f => ({ ...f, interest: e.target.value }))}
                    placeholder="e.g., Weight Loss" disabled={formLoading} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Reference</Label>
                  <Input value={form.reference} onChange={(e) => setForm(f => ({ ...f, reference: e.target.value }))}
                    placeholder="Who referred this lead?" disabled={formLoading} />
                </div>
              </div>

              {/* Assign Staff */}
              <div className="space-y-2">
                <Label className="text-xs">Assign Staff</Label>
                <MultiSelect
                  options={staffList.map(s => ({ label: s.name, value: s.id }))}
                  selected={form.assignedStaffIds}
                  onChange={(vals) => setForm(f => ({ ...f, assignedStaffIds: vals }))}
                  placeholder="Select staff members..."
                  className="h-10"
                />
              </div>

              {/* Notes */}
              <div className="space-y-1.5">
                <Label className="text-xs">Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Additional notes about this lead..." rows={3} disabled={formLoading} />
              </div>

            </div>
          </div>
          <DialogFooter className="px-5 py-4 border-t border-border bg-card shrink-0 flex flex-row items-center justify-end gap-2">
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={formLoading}>Cancel</Button>
            <Button onClick={handleSaveLead} disabled={formLoading} data-testid="save-lead-btn" className="min-w-[110px]">
              {formLoading
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{editing ? "Saving..." : "Adding..."}</>
                : editing ? "Update Lead" : "Add Lead"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Schedule Followup Modal ── */}
      <Dialog open={showFollowupForm} onOpenChange={(o) => { if (!followupLoading) setShowFollowupForm(o); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wide">Schedule Followup</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-1">
              <Label className="text-xs">Schedule Date <span className="text-destructive">*</span></Label>
              <Input type="date" value={followupForm.scheduledDate}
                onChange={(e) => setFollowupForm(f => ({ ...f, scheduledDate: e.target.value }))}
                disabled={followupLoading}
                data-testid="followup-form-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Schedule Time <span className="text-destructive">*</span></Label>
              <Input type="time" value={followupForm.scheduledTime}
                onChange={(e) => setFollowupForm(f => ({ ...f, scheduledTime: e.target.value }))}
                disabled={followupLoading}
                data-testid="followup-form-time" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Textarea value={followupForm.notes}
                onChange={(e) => setFollowupForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Followup notes..."
                rows={3}
                disabled={followupLoading} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFollowupForm(false)} disabled={followupLoading}>
              Cancel
            </Button>
            <Button onClick={handleSaveFollowup} disabled={followupLoading} data-testid="save-followup-btn" className="min-w-[150px]">
              {followupLoading
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Scheduling...</>
                : <><Calendar className="h-4 w-4 mr-2" />Schedule Followup</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirmation ── */}
      <DeleteConfirmModal
        open={showDeleteModal}
        onOpenChange={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
        onConfirm={handleDelete}
        title="Delete Lead"
        description="Are you sure you want to delete this lead? This action cannot be undone."
        itemName={deleteTarget?.name}
      />
    </div>
  );
}
